import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaSearch } from 'react-icons/fa';

import { apiService } from '../Config/api.service';
import { destinationsDe } from './navigation';

// Un collaborateur se cherche à partir de deux caractères, une fois la
// frappe posée : une requête par intention, pas par touche.
const LONGUEUR_MIN_COLLABORATEURS = 2;
const DELAI_COLLABORATEURS = 250;
const MAX_PAGES = 8;

const surMac = () =>
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent || '');

const normaliser = (texte) =>
  (texte || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

// Tous les termes doivent se trouver quelque part (libellé, contexte, mots du
// métier). Le libellé qui commence par la recherche passe devant, puis celui
// qui la contient, puis ce qu'un mot-clé seul a trouvé ; à rang égal, l'ordre
// du menu.
const rang = (destination, termes) => {
  const tout = normaliser([destination.libelle, destination.contexte, ...destination.motsCles].join(' '));
  if (!termes.every((terme) => tout.includes(terme))) return null;
  const libelle = normaliser(destination.libelle);
  const phrase = termes.join(' ');
  if (libelle.startsWith(phrase)) return 0;
  if (libelle.includes(phrase)) return 1;
  return 2;
};

const chercherPages = (destinations, requete) => {
  const termes = normaliser(requete).split(/\s+/).filter(Boolean);
  // Sans recherche, les pages du menu en accès rapide.
  if (termes.length === 0) return destinations.filter(({ type }) => type === 'page');
  return destinations
    .map((destination, ordre) => ({ destination, ordre, rang: rang(destination, termes) }))
    .filter((candidat) => candidat.rang !== null)
    .sort((a, b) => a.rang - b.rang || a.ordre - b.ordre)
    .slice(0, MAX_PAGES)
    .map(({ destination }) => destination);
};

/**
 * La recherche globale du gestionnaire (Yros/Kavero#416) : Ctrl K (Cmd K sur
 * Mac) ou le bouton de la barre du haut. Elle trouve une page, un onglet ou un
 * collaborateur, et y mène.
 */
const RechercheGlobale = ({ user }) => {
  const { t } = useTranslation();
  const naviguer = useNavigate();
  const idListe = useId();
  const [ouverte, setOuverte] = useState(false);
  const [requete, setRequete] = useState('');
  const [actif, setActif] = useState(0);
  const [collaborateurs, setCollaborateurs] = useState([]);
  const [chargement, setChargement] = useState(false);
  const boutonRef = useRef(null);
  const champRef = useRef(null);
  const precedent = useRef(null);
  const derniereRequete = useRef(0);

  const ouvrir = useCallback(() => setOuverte(true), []);

  useEffect(() => {
    const surRaccourci = (evenement) => {
      if ((evenement.ctrlKey || evenement.metaKey) && evenement.key?.toLowerCase() === 'k') {
        evenement.preventDefault();
        ouvrir();
      }
    };
    document.addEventListener('keydown', surRaccourci);
    return () => document.removeEventListener('keydown', surRaccourci);
  }, [ouvrir]);

  // À l'ouverture, le champ prend le focus ; on retient d'où l'on venait
  // pour l'y rendre à la fermeture.
  useEffect(() => {
    if (!ouverte) return;
    precedent.current = document.activeElement;
    champRef.current?.focus();
  }, [ouverte]);

  // Les collaborateurs, par la route de la liste (nom ou matricule). Chaque
  // frappe invalide la précédente : une réponse en retard ne remplace
  // jamais la bonne.
  useEffect(() => {
    const numero = ++derniereRequete.current;
    const terme = requete.trim();
    setCollaborateurs((courants) => (courants.length ? [] : courants));
    if (!ouverte || terme.length < LONGUEUR_MIN_COLLABORATEURS) {
      setChargement(false);
      return undefined;
    }
    setChargement(true);
    const minuterie = setTimeout(() => {
      apiService
        .getElement(`/employees?asearch=${encodeURIComponent(terme)}&limit=5`)
        .then((reponse) => {
          if (numero === derniereRequete.current) setCollaborateurs(reponse?.data?.employees || []);
        })
        .catch(() => {
          // Une erreur réseau ne doit pas empêcher de trouver les pages.
        })
        .finally(() => {
          if (numero === derniereRequete.current) setChargement(false);
        });
    }, DELAI_COLLABORATEURS);
    return () => clearTimeout(minuterie);
  }, [requete, ouverte]);

  const fermer = () => {
    setOuverte(false);
    setRequete('');
    setActif(0);
    const retour = precedent.current;
    (retour && retour !== document.body && retour.isConnected ? retour : boutonRef.current)?.focus();
  };

  const pages = ouverte ? chercherPages(destinationsDe(user, t), requete) : [];
  const resultats = [
    ...pages.map((destination) => ({
      cle: `page-${destination.id}`,
      groupe: 'pages',
      libelle: destination.libelle,
      detail: destination.contexte,
      lien: destination.lien,
    })),
    ...collaborateurs.map((collaborateur) => ({
      cle: `collaborateur-${collaborateur.id}`,
      groupe: 'collaborateurs',
      libelle: collaborateur.name,
      detail: [collaborateur.matricule, collaborateur.poste].filter(Boolean).join(' · '),
      lien: `/employees/${collaborateur.id}`,
    })),
  ];
  const indexActif = Math.min(actif, Math.max(resultats.length - 1, 0));
  const idOption = (index) => `${idListe}-option-${index}`;

  const aller = (lien) => {
    fermer();
    naviguer(lien);
  };

  const surTouche = (evenement) => {
    const nombre = resultats.length;
    switch (evenement.key) {
      case 'Escape':
        evenement.preventDefault();
        fermer();
        break;
      case 'ArrowDown':
        evenement.preventDefault();
        if (nombre) setActif((indexActif + 1) % nombre);
        break;
      case 'ArrowUp':
        evenement.preventDefault();
        if (nombre) setActif((indexActif - 1 + nombre) % nombre);
        break;
      case 'Enter':
        evenement.preventDefault();
        if (resultats[indexActif]) aller(resultats[indexActif].lien);
        break;
      case 'Tab':
        // Le panneau est modal : le focus reste dans le champ, les flèches
        // parcourent les résultats.
        evenement.preventDefault();
        break;
      default:
        break;
    }
  };

  const groupe = (id, titre) => {
    const membres = resultats
      .map((resultat, index) => ({ resultat, index }))
      .filter(({ resultat }) => resultat.groupe === id);
    if (membres.length === 0) return null;
    return (
      <div role="group" aria-label={titre} className="py-1">
        <p aria-hidden="true" className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
          {titre}
        </p>
        {membres.map(({ resultat, index }) => (
          <div
            key={resultat.cle}
            id={idOption(index)}
            role="option"
            aria-selected={index === indexActif}
            onClick={() => aller(resultat.lien)}
            onMouseMove={() => setActif(index)}
            className={`flex cursor-pointer flex-col gap-0.5 rounded-lg px-3 py-2 ${
              index === indexActif ? 'bg-[--bg-color] text-[--color-primary]' : 'text-gray-800'
            }`}
          >
            <span className="text-sm font-medium">{resultat.libelle}</span>
            {resultat.detail && <span className="text-xs text-gray-500">{resultat.detail}</span>}
          </div>
        ))}
      </div>
    );
  };

  const recherche = requete.trim();

  return (
    <>
      <button
        ref={boutonRef}
        type="button"
        onClick={ouvrir}
        aria-label={t('recherche.ouvrir')}
        aria-haspopup="dialog"
        aria-keyshortcuts="Control+K Meta+K"
        className="flex h-10 shrink-0 items-center gap-2 rounded-full border border-gray-200 bg-white px-4 text-sm text-gray-600 shadow-sm transition-colors hover:border-[--color-primary] hover:text-[--color-primary] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[--color-primary]"
      >
        <FaSearch aria-hidden="true" className="h-3.5 w-3.5" />
        <span className="hidden sm:inline">{t('recherche.ouvrir')}</span>
        <kbd className="hidden rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 font-sans text-[11px] font-medium text-gray-500 md:inline">
          {surMac() ? '⌘ K' : 'Ctrl K'}
        </kbd>
      </button>

      {ouverte && (
        <div className="fixed inset-0 z-[60] flex items-start justify-center px-4 pt-[12vh]">
          <div data-testid="recherche-fond" aria-hidden="true" className="absolute inset-0 bg-slate-900/40" onClick={fermer} />
          <div
            role="dialog"
            aria-modal="true"
            aria-label={t('recherche.titre')}
            onKeyDown={surTouche}
            className="relative w-full max-w-xl overflow-hidden rounded-2xl bg-white shadow-xl"
          >
            <div className="flex items-center gap-3 border-b border-gray-200 px-4">
              <FaSearch aria-hidden="true" className="h-4 w-4 shrink-0 text-gray-400" />
              <input
                ref={champRef}
                type="text"
                role="combobox"
                aria-label={t('recherche.titre')}
                aria-expanded="true"
                aria-controls={idListe}
                aria-autocomplete="list"
                aria-activedescendant={resultats.length ? idOption(indexActif) : undefined}
                value={requete}
                onChange={(evenement) => {
                  setRequete(evenement.target.value);
                  setActif(0);
                }}
                placeholder={t('recherche.placeholder')}
                autoComplete="off"
                spellCheck={false}
                className="h-14 w-full min-w-0 bg-transparent text-base text-gray-800 outline-none placeholder:text-gray-400"
              />
              <button
                type="button"
                tabIndex={-1}
                onClick={fermer}
                aria-label={t('recherche.fermer')}
                className="shrink-0 rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-500 hover:bg-gray-50"
              >
                {t('recherche.echap')}
              </button>
            </div>
            <div id={idListe} role="listbox" aria-label={t('recherche.resultats')} className="max-h-[60vh] overflow-y-auto p-2">
              {groupe('pages', t('recherche.pages'))}
              {groupe('collaborateurs', t('recherche.collaborateurs'))}
            </div>
            <div aria-live="polite" className="px-5 pb-3 text-sm text-gray-500 empty:hidden">
              {chargement && t('recherche.chargement')}
              {!chargement && recherche && resultats.length === 0 && t('recherche.aucunResultat', { terme: recherche })}
            </div>
            <p className="hidden border-t border-gray-100 px-5 py-2 text-xs text-gray-400 sm:block">{t('recherche.aide')}</p>
          </div>
        </div>
      )}
    </>
  );
};

export default RechercheGlobale;
